import { toast } from 'sonner'
import { fetchWidgetLeadFormById } from './leadFormsApi.js'

/** Question/intent menu items store API payloads like `question-trigger-123`. */
export function isMenuTriggerPayload(prompt) {
  const p = String(prompt || '').trim()
  return /^question-trigger-\d+$/.test(p) || /^intent-trigger-\d+$/.test(p)
}

/**
 * Execute a hamburger menu item (mirrors ButtonsMessage.performAction).
 *
 * @param {object} opts
 * @param {object} opts.item
 * @param {object} opts.ochatbot
 * @param {object} opts.conf
 * @param {(apiText: string, displayText: string) => void} [opts.onSendMenuAction] visitor bubble + callAPI (label vs payload)
 * @param {(text: string) => void} [opts.onSend] legacy plain send (custom prompts only when onSendMenuAction omitted)
 * @param {(form: object) => void} opts.openForm
 * @param {(forms: object[] | ((prev: object[]) => object[])) => void} [opts.setActiveLeadForms]
 */
export async function performMenuItemAction({
  item,
  ochatbot,
  conf,
  onSendMenuAction,
  onSend,
  openForm,
  setActiveLeadForms,
}) {
  if (!item) return

  const actionType = item.actionType || 'prompt'
  const label = item.label || ''

  if (actionType === 'url') {
    const url = item.url
    if (!url) return
    ochatbot?.callAPI(`ometrics-button-click~${url}`, label, true)
    window.open(url, '_blank', 'noopener')
    return
  }

  if (actionType === 'leadForm') {
    const formId = String(item.leadFormId || '')
    if (!formId) return
    const payload = `lead-form-trigger-${formId}`
    const activeLeadForms = conf?.activeLeadForms || conf?.leadForms || []
    const form = (Array.isArray(activeLeadForms) ? activeLeadForms : []).find(
      (f) => String(f.id) === formId,
    )
    if (form) {
      openForm(form)
      ochatbot?.callAPI(`ometrics-button-click~${payload}`, label, true)
      return
    }
    try {
      const list = await fetchWidgetLeadFormById({
        apiBaseUrl: conf?.apiBaseUrl,
        agentId: conf?.agentId || conf?.chatId,
        masterKey: conf?.masterKey,
        formId,
      })
      const fetched = Array.isArray(list)
        ? list.find((f) => String(f.id) === formId) || list[0]
        : null
      if (!fetched) {
        toast('This form is unavailable right now.')
        return
      }
      setActiveLeadForms?.((prev) => {
        const arr = Array.isArray(prev) ? prev : []
        if (arr.some((f) => String(f.id) === String(fetched.id))) return arr
        return [...arr, fetched]
      })
      openForm(fetched)
      ochatbot?.callAPI(`ometrics-button-click~${payload}`, label, true)
    } catch (err) {
      console.error('Menu lead form lookup failed', err)
      toast('Could not load the form. Please try again.')
    }
    return
  }

  const prompt = String(item.prompt || item.payload || label).trim()
  if (!prompt) return
  const display = (label || prompt).trim()
  if (onSendMenuAction) {
    onSendMenuAction(prompt, display)
    return
  }
  if (onSend && !isMenuTriggerPayload(prompt)) {
    onSend(prompt)
    return
  }
  ochatbot?.callAPI(prompt, display, isMenuTriggerPayload(prompt))
}

/**
 * @param {string} url
 * @param {string} [target] `_blank` for new tab; `_top` for same tab (breaks out of widget iframe).
 */
export function openMenuUrl(url, ochatbot, label = '', target = '_blank') {
  if (!url) return
  ochatbot?.callAPI(`ometrics-button-click~${url}`, label, true)
  const features = target === '_blank' ? 'noopener' : 'noopener,noreferrer'
  window.open(url, target, features)
}
